import { GuideLayout } from '@/components/layout/GuideLayout';
import { SEO } from '@/components/SEO';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

const HOWTO_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: '원클릭 AI 대량 소싱 사용 방법',
  description:
    'AI가 이미지 생성부터 상품 정보 작성까지 자동 처리하여 최대 100개 상품을 Shopee Global SKU에 대량 등록하는 원클릭 AI 대량 소싱 기능 사용 가이드',
  video: {
    '@type': 'VideoObject',
    name: '원클릭 AI 대량 소싱 사용 가이드 동영상',
    description: '셀잇파파 원클릭 AI 대량 소싱 기능 사용법 안내 영상',
    embedUrl: 'https://www.youtube.com/embed/d5GF-_BGUw0',
    uploadDate: '2025-08-27',
    thumbnailUrl: 'https://img.youtube.com/vi/d5GF-_BGUw0/hqdefault.jpg',
  },
  step: [
    { '@type': 'HowToStep', position: 1, name: '소싱 구독 구매 및 할당량 확인', text: '소싱 구독 바로가기 버튼으로 소싱 구독을 구매하면 계정에 소싱 할당량이 충전됩니다.' },
    { '@type': 'HowToStep', position: 2, name: '소싱할 상품 선택 및 대량 소싱 실행', text: '검색/필터로 원하는 상품을 최대 100개까지 선택한 뒤 대량 소싱 버튼을 클릭합니다.' },
    { '@type': 'HowToStep', position: 3, name: '소싱 옵션 설정 및 필수 확인 사항', text: '필수 확인 사항 동의 후 이미지 테두리 색상, 시그니처, 상품 설명 예시를 입력하고 소싱을 시작합니다.' },
    { '@type': 'HowToStep', position: 4, name: 'Global SKU 등록 상품 상세 검토', text: 'Shopee Seller Center의 Global SKU 메뉴에서 AI 생성 이미지, 상품 정보, 가격, 무게, 인증번호를 검토합니다.' },
    { '@type': 'HowToStep', position: 5, name: 'Mass Publish 일괄 퍼블리시', text: 'Mass Publish 기능으로 8개 국가 상점에 다수 상품을 일괄 퍼블리시합니다.' },
  ],
};

export default function GuideProductSourcing() {
  return (
    <>
      <SEO 
        title="원클릭 AI 대량 소싱 가이드"
        description="AI를 활용하여 이미지 생성부터 상품 정보 작성까지 자동으로 처리하여 다수의 상품을 신속하게 Shopee Global SKU에 대량 등록하세요"
        canonical="/guide/product-sourcing"
        ogTitle="원클릭 AI 대량 소싱 가이드 | 대량 상품 등록 - 셀잇파파"
        ogDescription="AI를 활용하여 이미지 생성부터 상품 정보 작성까지 자동으로 처리하여 다수의 상품을 신속하게 Shopee Global SKU에 대량 등록하세요"
        structuredData={HOWTO_SCHEMA}
      />
      <GuideLayout
        title="원클릭 AI 대량 소싱"
        description="AI를 활용하여 이미지 생성부터 상품 정보 작성까지 자동으로 처리하여 다수의 상품을 신속하게 Shopee Global SKU에 대량 등록하세요"
        videoUrl="https://youtu.be/d5GF-_BGUw0"
      >
      <div className="space-y-12">
        {/* 개요 섹션 */}
        <section className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg p-8 border border-primary/20">
          <h2 className="text-2xl font-bold mb-4 text-primary">🚀 시스템 개념</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card rounded-lg p-4 border">
              <h3 className="font-semibold mb-2">AI 이미지 자동 생성</h3>
              <p className="text-sm text-muted-foreground">저작권 문제가 없는 합법적인 상품 이미지를 AI가 자동으로 생성</p>
            </div>
            <div className="bg-card rounded-lg p-4 border">
              <h3 className="font-semibold mb-2">상품 정보 자동 작성</h3>
              <p className="text-sm text-muted-foreground">상품명, 설명, 상세 정보를 AI가 자동으로 생성 및 번역</p>
            </div>
            <div className="bg-card rounded-lg p-4 border">
              <h3 className="font-semibold mb-2">대량 등록 시스템</h3>
              <p className="text-sm text-muted-foreground">최대 100개 상품을 한 번에 선택하여 Global SKU에 일괄 등록</p>
            </div>
            <div className="bg-card rounded-lg p-4 border">
              <h3 className="font-semibold mb-2">소싱 할당량 관리</h3>
              <p className="text-sm text-muted-foreground">월간 구독 플랜에 따른 소싱 할당량 확인 및 사용량 추적</p>
            </div>
          </div>
        </section>

        {/* 소싱 옵션 설정 정보 - 데스크톱: 펼침, 모바일: 아코디언 */}
        {/* 모바일 (접힘 기본) */}
        <section className="bg-muted/30 rounded-lg p-4 md:hidden">
          <Accordion type="single" collapsible>
            <AccordionItem value="sourcing-options">
              <AccordionTrigger className="text-base font-semibold">
                ⚙️ 소싱 옵션 설정 항목
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 gap-3 pt-2">
                  <div className="bg-card rounded-lg p-3 border">
                    <h4 className="font-medium text-primary">이미지 테두리 색상</h4>
                    <p className="text-sm text-muted-foreground">생성될 이미지의 테두리 색상 선택</p>
                  </div>
                  <div className="bg-card rounded-lg p-3 border">
                    <h4 className="font-medium text-primary">시그니처 (상점 이름)</h4>
                    <p className="text-sm text-muted-foreground">상품명 끝과 이미지에 표시 (최대 10자)</p>
                  </div>
                  <div className="bg-card rounded-lg p-3 border">
                    <h4 className="font-medium text-primary">상품 설명 예시</h4>
                    <p className="text-sm text-muted-foreground">AI가 참고할 설명 스타일 (500~4000자)</p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>

        {/* 데스크톱 (항상 펼침) */}
        <section className="bg-muted/30 rounded-lg p-6 hidden md:block">
          <h3 className="text-lg font-semibold mb-4">⚙️ 소싱 옵션 설정 항목</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card rounded-lg p-3 border">
              <h4 className="font-medium text-primary">이미지 테두리 색상</h4>
              <p className="text-sm text-muted-foreground">생성될 이미지의 테두리 색상 선택</p>
            </div>
            <div className="bg-card rounded-lg p-3 border">
              <h4 className="font-medium text-primary">시그니처 (상점 이름)</h4>
              <p className="text-sm text-muted-foreground">상품명 끝과 이미지에 표시 (최대 10자)</p>
            </div>
            <div className="bg-card rounded-lg p-3 border">
              <h4 className="font-medium text-primary">상품 설명 예시</h4>
              <p className="text-sm text-muted-foreground">AI가 참고할 설명 스타일 (500~4000자)</p>
            </div>
          </div>
        </section>

        {/* 단계별 사용법 */}
        <section>
          <h2 className="text-3xl font-bold mb-8 text-center">🚀 단계별 사용법</h2>
          
          <div className="space-y-8">
            {/* 1단계 */}
            <div className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">1</div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-3">소싱 구독 구매 및 할당량 확인</h3>
                  <p className="text-muted-foreground mb-4">
                    원클릭 AI 대량 소싱 서비스는 별도의 유료 부가 서비스이며, 유료 플랜 이용자만 구매할 수 있습니다. 
                    화면 오른쪽 상단의 '소싱 구독 바로가기' 버튼을 통해 소싱 구독을 구매합니다.
                  </p>
                  <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-4">
                    <p className="text-sm"><strong>💡 핵심:</strong> 소싱 구독을 구매하면 계정에 소싱 할당량이 충전되며, 이후 원하시는 상품을 바로 소싱하거나, 이미 소싱된 상품을 제외하고 소싱할 수 있습니다.</p>
                  </div>
                  <Button asChild className="gap-2">
                    <Link to="/checkout" state={{
                      subscriptionType: 'sourcing',
                      planId: 'sourcing_500',
                      planName: '소싱 500',
                      billingCycle: 'monthly',
                      monthlyPrice: 240000,
                    }}>
                      <Sparkles className="h-4 w-4" />
                      소싱 구독 바로가기
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            {/* 2단계 */}
            <div className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">2</div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-3">소싱할 상품 선택 및 대량 소싱 실행</h3>
                  <p className="text-muted-foreground mb-4">
                    검색 또는 브랜드/카테고리 필터를 통해 원하는 상품만 보이도록 설정한 후, 현재 페이지에 있는 상품을 선택합니다. 그 후 '대량 소싱' 버튼을 클릭하여 소싱을 시작합니다.
                  </p>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-medium mb-2">선택 시 유의사항:</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• 최대 100개까지 선택 가능</li>
                      <li>• 소싱 할당량 내에서만 선택 가능</li>
                      <li>• '이미 소싱한 제품 제외' 체크박스로 중복 방지</li>
                      <li>• 선택 완료 후 '대량 소싱' 버튼 클릭</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 3단계 */}
            <div className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">3</div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-3">소싱 옵션 설정 및 필수 확인 사항</h3>
                  <p className="text-muted-foreground mb-4">
                    소싱 옵션 설정에서 필수 확인 사항에 동의하고, 이미지 테두리 색상, 시그니처(상점 이름), 상품 설명 예시를 입력합니다.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                      <h4 className="font-medium mb-2">🎨 이미지 테두리 색상</h4>
                      <p className="text-sm text-muted-foreground">생성될 이미지의 테두리 색상을 선택합니다.</p>
                    </div>
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                      <h4 className="font-medium mb-2">✍️ 시그니처 (상점 이름)</h4>
                      <p className="text-sm text-muted-foreground">상품명 끝과 이미지 우측 하단에 표시됩니다 (최대 10자).</p>
                    </div>
                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 md:col-span-2">
                      <h4 className="font-medium mb-2">📝 상품 설명 예시 (500~4000자)</h4>
                      <p className="text-sm text-muted-foreground">현재 사용 중인 상품 설명 예시를 입력하면 AI가 해당 스타일을 참고하여 유사한 톤으로 상품 설명을 자동 생성합니다.</p>
                    </div>
                  </div>
                  <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mt-4">
                    <p className="text-sm"><strong>⏱️ 소싱 시작:</strong> '소싱 시작' 버튼을 누르면 약 5~10분 후 S 등록hopee Global SKU에 상품이됩니다. "예약 완료"라는 문구가 떴으면 소싱이 진행되는 동안 다른 작업을 하셔도 됩니다.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 4단계 */}
            <div className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">4</div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-3">Global SKU 등록 상품 상세 검토</h3>
                  <p className="text-muted-foreground mb-4">
                    일정 시간이 지난 후, Shopee Seller Center의 Global SKU 메뉴에서 해당 제품이 등록된 것을 확인합니다.
                  </p>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-medium mb-2">필수 검토 항목:</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• <strong>AI 생성 이미지:</strong> 실제 상품과 일치하는지 확인하고 필요시 교체</li>
                      <li>• <strong>상품 제목 및 카테고리:</strong> 정확성 확인 및 수정</li>
                      <li>• <strong>상품 설명 및 상세 정보:</strong> 내용 검토 및 보완</li>
                      <li>• <strong>매입가 및 판매가:</strong> Global SKU Price는 기본적으로 매입가의 두 배로 자동 입력</li>
                      <li>• <strong>무게:</strong> 기본값 0.3kg, 실제 상품 기준에 맞게 수정</li>
                      <li>• <strong>인증번호:</strong> 기본값이 점(.)으로 표시, 실제 유효한 인증번호 직접 입력</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 5단계 */}
            <div className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">5</div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-3">Mass Publish를 통한 다수 상품 일괄 퍼블리시</h3>
                  <p className="text-muted-foreground mb-4">
                    다량의 상품이 등록된 경우, 각 상품을 하나씩 퍼블리시하는 것은 비효율적입니다. Mass Publish 기능을 활용합니다.
                  </p>
                  <div className="bg-muted/50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium mb-2">Mass Publish 사용 방법:</h4>
                    <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
                      <li>쇼피 셀러 센터 'Global SKU'메뉴의 화면 오른쪽 상단의 'Publish Shop Products' 내 'Mass Publish' 버튼 클릭</li>
                      <li>퍼블리시할 상점(국가)을 선택</li>
                      <li>여러 개의 제품을 선택하여 한 번에 해당 상점에 퍼블리시</li>
                      <li>8개 국가 상점에 각각 Mass Publish를 수행</li>
                    </ol>
                  </div>
                  <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                    <p className="text-sm"><strong>🌍 다국가 퍼블리시:</strong> 싱가포르, 말레이시아, 태국 등 8개 국가 상점에 각각 Mass Publish를 수행하여 다수의 상품을 일괄 퍼블리시할 수 있습니다.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 주의사항 섹션 */}
        <section className="bg-destructive/5 rounded-lg p-8 border border-destructive/20">
          <h2 className="text-2xl font-bold mb-6 text-destructive">⚠️ 주의사항</h2>
          
          <div className="space-y-6">
            {/* 옵션 상품 관련 */}
            <div className="bg-card rounded-lg p-4 border">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <span className="text-warning">📦</span> 옵션 상품 관련
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• 소싱 가능 상품 목록에 '#'이 포함된 상품명은 옵션 상품입니다.</li>
                <li>• 현재 옵션별 매입가 설정을 지원하지 않으므로 옵션 상품 1개가 단독 상품처럼 등록됩니다.</li>
                <li>• '#' 상품명은 옵션별 매입가 업데이트가 될 때까지 소싱하지 않는 것을 권장합니다. (26년 2월 업데이트 예정)</li>
              </ul>
            </div>

            {/* 법적·행정적 책임 사항 */}
            <div className="bg-card rounded-lg p-4 border">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <span className="text-destructive">⚖️</span> 법적·행정적 책임 사항
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-destructive/5 rounded-lg p-3">
                  <h4 className="font-medium mb-1">브랜드 제재</h4>
                  <p className="text-sm text-muted-foreground">Shopee 판매 금지 브랜드/제품은 계정 제재 및 판매 중단 가능</p>
                </div>
                <div className="bg-destructive/5 rounded-lg p-3">
                  <h4 className="font-medium mb-1">제품 이미지 및 IP</h4>
                  <p className="text-sm text-muted-foreground">무단 이미지·상표 사용은 저작권 침해로 제재 대상</p>
                </div>
                <div className="bg-destructive/5 rounded-lg p-3">
                  <h4 className="font-medium mb-1">제품 정보의 정확성</h4>
                  <p className="text-sm text-muted-foreground">상품명/성분/원산지가 사실과 다를 경우 법적 문제 발생</p>
                </div>
                <div className="bg-destructive/5 rounded-lg p-3">
                  <h4 className="font-medium mb-1">현지 인증 요건</h4>
                  <p className="text-sm text-muted-foreground">카테고리별 위생허가·안전성 인증 미비 시 판매 제한 가능</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      </GuideLayout>
    </>
  );
}
